import { Widget } from '../core/Widget.js';
import { ViewWidgetHelper } from '../view/ViewWidgetHelper.js';

class ComboBoxWidget extends Widget {
    constructor() {
        super(...arguments);
        this._searchSeq = 0;
        // состояние
        this.menuWrap = null;
        this.menuList = null;
        this.isOpen = false;
        this.activeIndex = -1;
        this.items = [];
        this.lastTerm = "";
        // куда поставить курсор при открытии: первый или последний
        this.openInit = null;
        // привязанные слушатели
        this.onKeydown = (e) => this._onKeydown(e);
        this.onInput = () => this._onInput();
        this.onChange = () => this._onChange();
        this.onDocMouseDown = (e) => this._onDocMouseDown(e);
        this.onListClick = (e) => this._onListClick(e);
        this.onListMouseDown = (e) => { e.preventDefault(); };
    }
    static _cssKey() { return "im-combo"; }
    /** Полная инициализация (вызовется базовым классом один раз) */
    _create(options) {
        // 1) нормализация и валидация опций
        this._opts = this._normalizeOptions(options);
        // 2) начальный data-title = текущее визуальное значение (совместимость)
        if (!this.element.getAttribute("data-title")) {
            this.element.setAttribute("data-title", this.element.value ?? "");
        }
        // 3) контейнер дропа
        const host = this._resolveAppendTo(this._opts.appendTo ?? this._opts.target);
        // wrapper (оставляем для позиционирования, но добавим ui-front)
        this.menuWrap = document.createElement("div");
        this.menuWrap.className = "im-combo-menu ui-front";
        this.menuWrap.style.position = "absolute";
        this.menuWrap.style.display = "none";
        this.menuWrap.style.zIndex = "9999";
        // UL с jQuery UI классами
        this.menuList = document.createElement("ul");
        this.menuList.className = "ui-autocomplete ui-front ui-menu ui-widget ui-widget-content";
        this.menuList.setAttribute("role", "listbox");
        if (!this.menuList.id) {
            this.menuList.id = `ui-id-${ComboBoxWidget._idSeed++}`;
        }
        this.menuWrap.appendChild(this.menuList);
        host.appendChild(this.menuWrap);
        // 5) подписки
        this.element.addEventListener("keydown", this.onKeydown);
        this.element.addEventListener("input", this.onInput);
        this.element.addEventListener("change", this.onChange);
        document.addEventListener("mousedown", this.onDocMouseDown);
        this.menuList.addEventListener("click", this.onListClick);
        this.menuList.addEventListener("mousedown", this.onListMouseDown);
        // 6) кнопка-переключатель «показать всё»
        const next = this.element.nextElementSibling;
        if (next && next.classList.contains("im-combo-box-toggle")) {
            this.onToggleClick = () => { void this.search("   "); };
            next.addEventListener("click", this.onToggleClick);
        }
    }
    /** Программный поиск/открытие меню */
    async search(term) {
        this.lastTerm = term;
        const seq = ++this._searchSeq; // <- номер текущего запроса
        this._emit("source-request");
        try {
            const items = await this._opts.provider(term);
            // рендерим только если это самый свежий запрос и term не переехал
            if (seq !== this._searchSeq || this.lastTerm !== term)
                return;
            this.items = items ?? [];
            this._render();
            this._emit("source-response");
        }
        catch (e) {
            this._emit("source-err");
            console.error("ComboBoxWidget: search error", e);
        }
    }
    /** Клавиатура в инпуте: Tab/Enter/Escape и стрелки */
    _onKeydown(evt) {
        // Tab = выбрать активный пункт, если открыт список
        if (evt.key === "Tab" && this.isOpen && this.activeIndex >= 0) {
            evt.preventDefault();
            this._selectByIndex(this.activeIndex);
            return;
        }
        // Если меню закрыто: ↓ открывает меню
        if (!this.isOpen) {
            if (evt.key === "ArrowDown" || evt.key === "ArrowUp") {
                evt.preventDefault();
                const term = this._currentTerm();
                const tooShort = term.length < (this._opts.minlength ?? 2);
                this.openInit = (evt.key === "ArrowUp") ? "last" : "first";
                void this.search(tooShort ? "   " : term); // «показать всё» либо по терму
            }
            return; // Остальные клавиши — по умолчанию
        }
        // Меню открыто: стрелками двигаем активный пункт
        if (evt.key === "ArrowDown") {
            evt.preventDefault();
            this._moveActive(1);
            return;
        }
        if (evt.key === "ArrowUp") {
            evt.preventDefault();
            this._moveActive(-1);
            return;
        }
        // Быстрые прыжки (не обязательно, но приятно)
        if (evt.key === "Home") {
            evt.preventDefault();
            this.activeIndex = 0;
            this._syncActive();
            return;
        }
        if (evt.key === "End") {
            evt.preventDefault();
            this.activeIndex = this.items.length - 1;
            this._syncActive();
            return;
        }
        if (evt.key === "PageDown") {
            evt.preventDefault();
            this._moveActive(5);
            return;
        }
        if (evt.key === "PageUp") {
            evt.preventDefault();
            this._moveActive(-5);
            return;
        }
        // Enter = выбрать
        if (evt.key === "Enter" && this.activeIndex >= 0) {
            evt.preventDefault();
            this._selectByIndex(this.activeIndex);
            return;
        }
        // Esc = закрыть только список. 2026-09-15: событие поглощается, чтобы тот же Escape
        // не закрыл панель aside (ModalAside), popover или не отменил редактирование (CmdPanelWidget)
        if (evt.key === "Escape") {
            evt.preventDefault();
            evt.stopPropagation();
            this._hide();
            return;
        }
    }
    /** Реакция на ввод: поиск начиная с minlength */
    _onInput() {
        const term = this._currentTerm();
        if (term.length < (this._opts.minlength ?? 2)) {
            this._hide();
            return;
        }
        void this.search(term);
    }
    /** Нормализация значения + эмиссия combochange (совместимость) */
    _onChange() {
        const v0 = this.element.value ?? "";
        const v1 = v0.trim();
        if (v1 !== v0)
            this.element.value = v1;
        if (this.element.value.endsWith(","))
            this.element.value = this.element.value.slice(0, -1);
        if (this._opts.mode === "indexlist") {
            const m = this.element.value.match(/\[(\d+)\]/gm) ?? [];
            const braces = m.map(s => `{${s.substring(1, s.length - 1)}}`).join("");
            this.element.setAttribute("data-value", braces);
            this._emit("combochange");
            return;
        }
        if (this._opts.mode !== "freetext" && !this._opts.multiple) {
            if (this.element.value === "") {
                this.element.setAttribute("data-value", "");
                this._emit("combochange");
            }
            else {
                this.element.value = this.element.getAttribute("data-title") ?? this.element.value;
            }
            return;
        }
        if (!this._opts.multiple) {
            if (this.element.value !== this.element.getAttribute("data-value")) {
                this.element.setAttribute("data-value", this.element.value);
                this._emit("combochange");
            }
            return;
        }
        this._emit("combochange");
        this.element.classList.remove("ui-autocomplete-loading");
    }
    /** Закрытие по клику вне */
    _onDocMouseDown(e) {
        if (!this.isOpen)
            return;
        const t = e.target;
        if (t && (this.menuWrap?.contains(t) || this.element.contains(t)))
            return;
        this._hide();
    }
    /** Клик по пункту списка */
    _onListClick(e) {
        const wrap = e.target.closest(".ui-menu-item-wrapper");
        if (!wrap)
            return;
        const idx = Number(wrap.dataset.index);
        this._selectByIndex(idx);
    }
    /** Текущий терм поиска (последний токен в multiple) */
    _currentTerm() {
        const raw = this.element.value ?? "";
        if (!this._opts.multiple)
            return raw;
        const parts = this._splitMultiple(raw);
        return (parts.pop() ?? "").trim();
    }
    /** Разбиение строки для multiple-режима */
    _splitMultiple(text) {
        if (this.element.tagName.toLowerCase() === "textarea")
            return text.split("\n").map(s => s.trim());
        return (this._opts.semicolon ? text.split(",") : text.split(";")).map(s => s.trim());
    }
    // ── helper: вернуть фокус и поставить каретку в конец
    _refocusCaretToEnd() {
        const el = this.element;
        if (el.disabled || el.readOnly)
            return;
        // Делаем после закрытия меню и завершения клика мышью
        requestAnimationFrame(() => {
            el.focus();
            try {
                const pos = el.value.length;
                el.setSelectionRange(pos, pos);
            }
            catch { /* setSelectionRange может отсутствовать на очень старых браузерах */ }
        });
    }
    /** Отрисовка меню */
    _render() {
        if (!this.menuList || !this.menuWrap)
            return;
        this.menuList.innerHTML = "";
        if (!this.items.length) {
            this._hide();
            return;
        }
        this.items.forEach((it, i) => {
            const li = document.createElement("li");
            li.className = "ui-menu-item";
            li.setAttribute("role", "presentation");
            const wrap = document.createElement("div");
            wrap.className = "ui-menu-item-wrapper im-combo-item";
            wrap.setAttribute("role", "option");
            wrap.id = `${this.menuList.id}-item-${i}`;
            wrap.dataset.index = String(i);
            if (this._opts.htmlmode || (this._opts.autoHtml ?? true) && this._isHtmlValue(it.value)) {
                const safe = this._opts.sanitizeHtml ? this._opts.sanitizeHtml(it.value) : it.value;
                wrap.innerHTML = safe;
            }
            else {
                wrap.textContent = it.value;
            }
            li.appendChild(wrap);
            this.menuList.appendChild(li);
        });
        this.activeIndex = this.items.length
            ? (this.openInit === "last" ? this.items.length - 1 : 0)
            : -1;
        this.openInit = null; // ← сбросить «намерение» открытия
        this._syncActive();
        this._open();
    }
    /** Открыть меню */
    _open() {
        if (this.isOpen)
            return;
        this.isOpen = true;
        if (this.menuWrap) {
            this.menuWrap.style.display = "block";
            this._reposition();
        }
        if (this._opts.menuclass)
            document.body.classList.add(this._opts.menuclass);
        this.element.setAttribute("aria-controls", this.menuList.id);
        this.element.setAttribute("aria-expanded", "true");
        // this.element.setAttribute("data-value", "");
        this.element.classList.remove("ui-corner-all");
        this.element.classList.add("ui-corner-top");
    }
    /** Спрятать меню */
    /** Спрятать меню */
    _hide() {
        if (!this.isOpen)
            return;
        this.isOpen = false;
        // Снимаем активность у пунктов, чтобы тесты (и экранные читалки)
        // не «видели» старый .ui-state-active после закрытия.
        if (this.menuList) {
            this.menuList
                .querySelectorAll(".ui-menu-item-wrapper.ui-state-active")
                .forEach(el => {
                el.classList.remove("ui-state-active");
                el.setAttribute("aria-selected", "false");
            });
        }
        if (this.menuWrap)
            this.menuWrap.style.display = "none";
        if (this._opts.menuclass)
            document.body.classList.remove(this._opts.menuclass);
        this.element.setAttribute("aria-expanded", "false");
        this.element.removeAttribute("aria-activedescendant");
        this.element.classList.remove("ui-corner-top");
        this.element.classList.add("ui-corner-all");
        this.activeIndex = -1;
        this.openInit = null; // safety reset
    }
    /** Позиционирование меню под инпутом */
    _reposition() {
        if (!this.menuWrap)
            return;
        const r = this.element.getBoundingClientRect();
        const top = r.bottom + (window.pageYOffset || document.documentElement.scrollTop);
        const left = r.left + (window.pageXOffset || document.documentElement.scrollLeft);
        this.menuWrap.style.top = `${top}px`;
        this.menuWrap.style.left = `${left}px`;
        this.menuWrap.style.minWidth = `${r.width}px`;
    }
    /** Изменить активный пункт по шагу */
    _moveActive(step) {
        if (!this.items.length)
            return;
        const len = this.items.length;
        this.activeIndex = (this.activeIndex + step + len) % len;
        this._syncActive();
    }
    /** Проставить .is-active и прокрутить в видимую область */
    _syncActive() {
        if (!this.menuList)
            return;
        // снять активность + aria-selected
        this.menuList.querySelectorAll(".ui-menu-item-wrapper.ui-state-active")
            .forEach(el => {
            el.classList.remove("ui-state-active");
            el.setAttribute("aria-selected", "false");
        });
        if (this.activeIndex < 0) {
            this.element.removeAttribute("aria-activedescendant");
            return;
        }
        const wrap = this.menuList.querySelector(`#${this.menuList.id}-item-${this.activeIndex}`);
        if (wrap) {
            wrap.classList.add("ui-state-active");
            wrap.setAttribute("aria-selected", "true");
            wrap.scrollIntoView({ block: "nearest" });
            this.element.setAttribute("aria-activedescendant", wrap.id);
        }
    }
    /** Выбор элемента по индексу (multiple/одиночный режимы) */
    _selectByIndex(index) {
        const item = this.items[index];
        if (!item)
            return;
        if (this._opts.multiple) {
            const parts = this._splitMultiple(this.element.value ?? "");
            parts.pop();
            const text = this._displayText(item);
            if (item.value === item.key)
                parts.push(text);
            else
                parts.push(`${text} [${item.key}]`);
            parts.push(""); // завершающий разделитель
            const glue = this.element.tagName.toLowerCase() === "textarea" ? "\n" : (this._opts.semicolon ? ", " : "; ");
            this.element.value = parts.join(glue);
            if (this._opts.mode === "indexlist") {
                const m = this.element.value.match(/\[(\d+)\]/gm) ?? [];
                const res = m.map(s => `{${s.substring(1, s.length - 1)}}`).join("");
                this.element.setAttribute("data-value", res);
            }
            this.element.classList.remove("ui-autocomplete-loading");
            this._emit("combochange", { item, index });
            this._hide();
            this._refocusCaretToEnd();
            return;
        }
        // одиночный выбор
        const plain = this._displayText(item);
        this.element.value = plain;
        this.element.setAttribute("data-title", plain);
        this.element.setAttribute("data-value", item.key);
        if (item.url) {
            // если уходим по ссылке — фокус уже не нужен
            window.location.href = item.url;
        }
        this._emit("combochange", { item, index });
        this.element.dispatchEvent(new Event("change", { bubbles: true }));
        this._hide();
        this._refocusCaretToEnd();
    }
    /**
     * Резолв контейнера для меню:
     *  - если получен HTMLElement — используем его
     *  - если строка — querySelector
     *  - иначе: ближайший `.im-view .h0` → `document.body`
     */
    _resolveAppendTo(target) {
        if (target)
            return (typeof target === "string" ? (document.querySelector(target) ?? document.body) : target);
        const view = this.element.closest(".im-view");
        if (view) {
            const h0 = view.querySelector(".h0:last-of-type");
            if (h0)
                return h0;
        }
        return document.body;
    }
    /** emit DOM-событий (совместимость со старым `.trigger`) */
    _emit(name, detail) {
        this.element.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    }
    /** Нормализация/валидация входных опций */
    _normalizeOptions(o) {
        if (!o.provider) {
            o.provider = o.testProvider ? testProvider : makeDefaultProvider(this.element);
        }
        if (typeof o.provider !== "function") {
            throw new Error("[ComboBoxWidget] options.provider must be a function");
        }
        return {
            provider: o.provider,
            htmlmode: !!o.htmlmode,
            minlength: o.minlength ?? 2,
            multiple: !!o.multiple,
            semicolon: !!o.semicolon,
            mode: o.mode ?? "default",
            menuclass: o.menuclass ?? "",
            appendTo: o.appendTo,
            target: o.target,
            autoHtml: o.autoHtml ?? true,
            sanitizeHtml: o.sanitizeHtml ?? ((h) => h),
        };
    }
    /** Определить, содержит ли строка HTML-теги (безопасный быстрый детект). */
    _isHtmlValue(s) {
        return /<\/?[a-z][\s\S]*>/i.test(s);
    }
    /** Получить текст, который должен попасть в input для выбранного пункта. */
    _displayText(item) {
        // 1) если провайдер дал явный text — он в приоритете
        if (typeof item.text === "string" && item.text.trim() !== "") {
            return item.text.trim();
        }
        // 2) если нет HTML — просто вернуть value
        if (!this._isHtmlValue(item.value))
            return item.value;
        // 3) извлечь текст из HTML: <img alt="..."> → alt, остальные теги → текст
        return this._htmlToText(item.value);
    }
    /** Преобразовать HTML в «чистый» текст (учитываем alt у <img>, декодируем сущности). */
    _htmlToText(html) {
        if (!ComboBoxWidget._scratchDiv) {
            ComboBoxWidget._scratchDiv = document.createElement("div");
        }
        const div = ComboBoxWidget._scratchDiv;
        // при наличии санитайзера — пропускаем через него
        const safe = this._opts.sanitizeHtml ? this._opts.sanitizeHtml(html) : html;
        div.innerHTML = safe;
        // заменить <img> на их alt-текст (или удалить)
        div.querySelectorAll("img").forEach(img => {
            const alt = (img.getAttribute("alt") || "").trim();
            img.replaceWith(document.createTextNode(alt));
        });
        // забрать текст, схлопнуть пробелы
        const text = (div.textContent || "").replace(/\s+/g, " ").trim();
        // очистить контейнер (на всякий)
        div.textContent = "";
        return text;
    }
    /** Вернуть основное значение виджета. */
    getValue() {
        // indexlist — берём data-value вида "{1}{2}{3}" и возвращаем ["1","2","3"]
        if (this._opts.mode === "indexlist") {
            const dv = this.element.getAttribute("data-value")?.trim() ?? "";
            if (dv === "")
                return null;
            const out = [];
            dv.replace(/\{([^}]*)\}/g, (_m, g1) => {
                out.push(String(g1));
                return "";
            });
            return out.length ? out : null;
        }
        // multiple: сервер обычно ждёт видимый текст (как и в jQuery-версии)
        if (this._opts.multiple) {
            const s = this.element.value.trim();
            return s === "" ? null : s;
        }
        // single:
        // default: основной источник — data-value (key)
        // freetext: если data-value пуст, возвращаем сам текст
        const dv = this.element.getAttribute("data-value")?.trim() ?? "";
        if (dv !== "")
            return dv;
        if (this._opts.mode === "freetext") {
            const s = this.element.value.trim();
            return s === "" ? null : s;
        }
        return null;
    }
    /** Установить основное значение. Возвращает true — значение обработано. */
    setValue(val) {
        const isTA = this._isTextarea();
        // ── очистка
        if (val == null || val == '' || (typeof val === "string" && val.trim().toLowerCase() === "dbnull")) {
            this.element.value = "";
            this.element.setAttribute("data-value", "");
            this.element.setAttribute("data-title", "");
            return true;
        }
        // ── indexlist: в data-value — фигурные; в textarea — текст-токены построчно
        if (this._opts.mode === "indexlist") {
            const ids = Array.isArray(val) ? val.map(String) : this._parseIndexListString(String(val));
            const encoded = this._encodeIndexList(ids);
            this.element.setAttribute("data-value", encoded);
            if (isTA) {
                this.element.value = this._makeIndexListDisplay(ids); // "[1]\n[2]\n..."
                this.element.setAttribute("data-title", this.element.value);
            }
            return true;
        }
        // ── multiple (not indexlist): это "видимый текст"
        if (this._opts.multiple) {
            const s = Array.isArray(val)
                ? this._joinTokensForInput(val.map(String), isTA)
                : String(val).trim();
            this.element.value = s;
            // data-value в not-indexlist multiple не ведём
            return true;
        }
        // ── single + freetext: синхронизируем всё
        if (this._opts.mode === "freetext") {
            const s = Array.isArray(val)
                ? this._joinTokensForInput(val.map(String), isTA)
                : String(val).trim();
            this.element.value = s;
            this.element.setAttribute("data-value", s);
            this.element.setAttribute("data-title", s);
            return true;
        }
        // ── single + default (key/value):
        if (Array.isArray(val)) {
            // неожиданно пришёл массив — рендерим как текст (TA: по строкам; input: по разделителю)
            const s = this._joinTokensForInput(val.map(String), isTA);
            this.element.value = s;
            this.element.setAttribute("data-value", ""); // сброс старого key
            this.element.setAttribute("data-title", s);
            return true;
        }
        else {
            // обычный кейс: кладём key; для textarea ещё и показываем его как текст
            const raw = String(val).trim();
            this.element.setAttribute("data-value", raw);
            if (isTA) {
                this.element.value = raw;
                this.element.setAttribute("data-title", raw);
            }
            return true;
        }
    }
    /** true, если элемент — <textarea> */
    _isTextarea() {
        return this.element.tagName.toLowerCase() === "textarea";
    }
    /** Склейка токенов для input/textarea с учётом настроек разделителя. */
    _joinTokensForInput(tokens, isTA) {
        if (isTA)
            return tokens.join("\n");
        // для input — тот же разделитель, что и при выборе из меню
        const glue = this._opts.semicolon ? ", " : "; ";
        return tokens.join(glue);
    }
    /** Отрисовка indexlist для textarea: "[1]\n[2]\n..." */
    _makeIndexListDisplay(ids) {
        return ids.map(id => `[${id}]`).join("\n");
    }
    /** Превратить ["1","2","3"] → "{1}{2}{3}" (пустые/пробельные отбрасываем). */
    _encodeIndexList(arr) {
        const parts = arr.map(s => String(s).trim()).filter(Boolean);
        return parts.map(p => `{${p}}`).join("");
    }
    /** Разобрать строку "{1}{2}{3}" | "1,2,3" | "1 2 3" → ["1","2","3"]. */
    _parseIndexListString(s) {
        const str = s.trim();
        if (!str)
            return [];
        // Если уже фигурные — вытащим содержимое
        const brace = [...str.matchAll(/\{([^}]*)\}/g)].map(m => m[1].trim()).filter(Boolean);
        if (brace.length)
            return brace;
        // Иначе — просто находим группы цифр/символов между разделителями
        const plain = str.match(/[^\s,;]+/g) ?? [];
        return plain.map(x => x.trim()).filter(Boolean);
    }
    /** Присутствует ли основное значение. */
    hasValue() {
        if (this._opts.mode === "indexlist")
            return (this.element.getAttribute("data-value") ?? "") !== "";
        if (this._opts.multiple)
            return this.element.value.trim() !== "";
        if (this._opts.mode === "freetext")
            return this.element.value.trim() !== "";
        // default single
        return (this.element.getAttribute("data-value") ?? "") !== "";
    }
    /** Очистка ресурсов (идемпотентно) */
    _onDestroy() {
        try {
            this._hide();
        }
        catch { }
        try {
            this.element.removeEventListener("keydown", this.onKeydown);
        }
        catch { }
        try {
            this.element.removeEventListener("input", this.onInput);
        }
        catch { }
        try {
            this.element.removeEventListener("change", this.onChange);
        }
        catch { }
        try {
            document.removeEventListener("mousedown", this.onDocMouseDown);
        }
        catch { }
        try {
            this.menuList?.removeEventListener("click", this.onListClick);
        }
        catch { }
        try {
            this.menuList?.removeEventListener("mousedown", this.onListMouseDown);
        }
        catch { }
        const next = this.element.nextElementSibling;
        if (next && this.onToggleClick) {
            try {
                next.removeEventListener("click", this.onToggleClick);
            }
            catch { }
            this.onToggleClick = undefined;
        }
        try {
            this.menuWrap?.remove();
        }
        catch { }
        this.menuList = null;
        this.menuWrap = null;
    }
}
/** Имя виджета */
ComboBoxWidget._alias = "ComboBox";
ComboBoxWidget._idSeed = 1;
// Реиспользуемый контейнер для безопасного парсинга HTML → textContent
ComboBoxWidget._scratchDiv = null;
// TODO: Потом вынести в отдельный файл, что бы не билдилось в основную сборку?
/** Тестовые данные (часть — с HTML для проверки htmlmode) */
const DATA = [
    { key: "1", value: "Apple" },
    { key: "2", value: "Apricot" },
    { key: "3", value: "Avocado" },
    { key: "4", value: "<img src='/src/vs/img/nordbox-logo.svg' width='16' height='16' style='vertical-align:middle;margin-right:6px'> Alice Johnson" },
    { key: "5", value: "Banana" },
    { key: "6", value: "Blackberry" },
    { key: "7", value: "Blueberry" },
    { key: "8", value: "Cherry" },
    { key: "9", value: "Clementine" },
    { key: "10", value: "Cranberry" },
    { key: "11", value: "Date" },
    { key: "12", value: "Dragonfruit" },
    { key: "13", value: "Grape" },
    { key: "14", value: "Grapefruit" },
    { key: "15", value: "Guava" },
    { key: "16", value: "Kiwi" },
    { key: "17", value: "Lemon" },
    { key: "18", value: "Lime" },
    { key: "19", value: "Mango" },
    { key: "20", value: "Melon" },
    { key: "21", value: "Orange" },
    { key: "22", value: "Papaya" },
    { key: "23", value: "Peach" },
    { key: "24", value: "Pear" },
    { key: "25", value: "Pineapple" },
    { key: "26", value: "Plum" },
    { key: "27", value: "Pomegranate" },
    { key: "28", value: "Raspberry" },
    { key: "29", value: "Strawberry" },
    { key: "30", value: "Watermelon" },
];
/** Утилита: убрать HTML, чтобы поиск шёл по тексту даже при htmlmode */
function stripHtml(s) {
    return s.replace(/<[^>]+>/g, "");
}
/** Утилита: нормализация строки (нижний регистр + убираем диакритику) */
function norm(s) {
    return s
        .toLowerCase()
        .normalize("NFKD")
        .replace(/\p{Diacritic}/gu, "");
}
/**
 * Тестовый провайдер автодополнения.
 * @param term Последний токен для поиска (виджет уже отдаёт только его)
 * @returns Промис со списком опций
 */
function testProvider(term) {
    const q = term.trim();
    // «Показать всё» — как в legacy, если терм равен трём пробелам
    const showAll = term === "   ";
    // Имитируем сетевую задержку
    const delay = (ms) => new Promise(r => setTimeout(r, ms));
    return (async () => {
        await delay(180);
        if (showAll || q === "") {
            // Отдаём первые 20 элементов
            return DATA.slice(0, 20);
        }
        const nq = norm(q);
        const res = DATA.filter(it => norm(stripHtml(it.value)).includes(nq));
        // Лёгкая сортировка: точные начала строки выше
        res.sort((a, b) => {
            const aa = norm(stripHtml(a.value));
            const bb = norm(stripHtml(b.value));
            const aStarts = aa.startsWith(nq) ? 0 : 1;
            const bStarts = bb.startsWith(nq) ? 0 : 1;
            return aStarts - bStarts || aa.localeCompare(bb);
        });
        // Ограничим выдачу (например, до 20)
        return res.slice(0, 20);
    })();
}
/** (Необяз.) нормализуем серверный item к ComboItem */
function normalizeServerItem(raw) {
    if (!raw)
        return null;
    // Ищем ключ и отображаемое значение в разных возможных полях
    const key = raw.key ?? raw.Key ?? raw.id ?? raw.Id ?? raw.ID ?? raw.valueKey ?? raw.ValueKey ?? raw.value ?? raw.Value;
    const value = raw.value ?? raw.Value ?? raw.label ?? raw.Label ?? raw.html ?? raw.Html ?? raw.text ?? raw.Text ?? raw.title ?? raw.Title ?? raw.name ?? raw.Name;
    const url = raw.url ?? raw.Url ?? raw.href ?? raw.Href;
    if (key == null && value == null)
        return null;
    const item = {
        key: String(key ?? value ?? ""),
        value: String(value ?? key ?? ""),
    };
    if (url != null)
        item.url = String(url);
    // Если сервер прислал «чистый текст» отдельно — положим в text (опционально)
    const plain = raw.text ?? raw.Text ?? raw.plain ?? raw.Plain;
    if (plain != null)
        item.text = String(plain);
    return item;
}
/**
 * Фабрика провайдера автодополнения, работающего через стандартный серверный вызов.
 *
 * @param element  Инпут/textarea виджета
 * @param buildOptions  Кастомизатор payload-а (по умолчанию `{ term }`)
 * @returns provider(term) → Promise<ComboItem[]>
 */
function makeDefaultProvider(element) {
    return (term) => new Promise((resolve, reject) => {
        // 1) как в legacy: requestItem = ближайший .im-inputcollection или сам элемент
        const ic = element.closest(".im-inputcollection");
        const requestItem = ic ?? element;
        // 2) собираем options (по умолчанию только term)
        const options = { term };
        // 3) вызываем jQuery imView совместимым способом
        ViewWidgetHelper.ExecCmd(requestItem, {
            cmd: 'get-autocomplete',
            options: options,
            onSuccess: (data) => {
                try {
                    const raw = data?.items ?? data?.Items ?? data ?? [];
                    const list = Array.isArray(raw) ? raw : [];
                    const items = [];
                    for (const r of list) {
                        const it = normalizeServerItem(r);
                        if (it)
                            items.push(it);
                    }
                    resolve(items);
                }
                catch (e) {
                    reject(e);
                }
            },
            onError: (err) => {
                reject(err ?? new Error("get-autocomplete failed"));
            }
        });
    });
}

export { ComboBoxWidget, makeDefaultProvider, testProvider };
